import {
	Box,
	Flex,
	type FlexProps,
	type MantineStyleProp,
} from "@mantine/core";
import { forwardRef } from "react";
export type MagicBoxProps = {
	/**
	 * If true, the box will be centered in its parent.
	 * @default false
	 */
	center?: boolean;
	/**
	 * If true, the box will be positioned at the top left corner of its parent.
	 * @default false
	 */
	absTopLeft?: boolean;
	absBottomRight?: boolean;

	children: React.ReactNode;
	className?: string;
	gap?: FlexProps["gap"];
	column?: boolean;
	border?: boolean;
	p?: FlexProps["p"];
	py?: FlexProps["py"];
	pt?: FlexProps["pt"];
	scroll?: boolean;
	scrollY?: boolean;
	row?: boolean;
	top?: boolean;
	right?: boolean;
	left?: boolean;
	centerX?: boolean;
	centerY?: boolean;
	fullHeight?: boolean;
	fullWidth?: boolean;
	mah?: FlexProps["mah"];
	maw?: FlexProps["maw"];
	bg?: FlexProps["c"];
	nowrap?: boolean;
	wrap?: boolean;
	shrink?: boolean;
	noshrink?: boolean;
	grow?: boolean;
	nogrow?: boolean;
	flex?: number;
	spaceBetween?: boolean;
	spaceEvenly?: boolean;
	overflowHidden?: boolean;
	relative?: boolean;
	z?: number;
	mt?: FlexProps["mt"];
	mb?: FlexProps["mb"];
	w?: FlexProps["w"];
	h?: FlexProps["h"];
	style?: React.CSSProperties;
	bottom?: boolean;
	"data-testid"?: string;
};
export const MagicBox = forwardRef(
	(
		{
			center,
			absTopLeft,
			absBottomRight,
			children,
			gap,
			column,
			border,
			p,
			py,
			pt,
			scroll,
			scrollY,
			row,
			top,
			right,
			left,
			centerX,
			centerY,
			fullHeight,
			fullWidth,
			mah,
			nowrap,
			wrap,
			shrink,
			noshrink,
			grow,
			nogrow,
			flex,
			spaceBetween,
			spaceEvenly,
			overflowHidden,
			relative,
			z,
			bg,
			mt,
			mb,
			maw,
			w,
			style,
			bottom,
			h,
			className,
			"data-testid": dataTestId,
		}: MagicBoxProps,
		ref: React.Ref<HTMLDivElement>,
	) => {
		const styles: MantineStyleProp = {};

		if (scroll) {
			styles.overflow = "auto";
		}

		if (scrollY) {
			styles.overflowY = "auto";
		}

		if (fullHeight) {
			styles.height = "100%";
		}

		if (fullWidth) {
			styles.width = "100%";
		}

		if (column) {
			styles.display = "flex";
			styles.flexDirection = "column";
		}

		if (row) {
			styles.display = "flex";
			styles.flexDirection = "row";
		}

		if (flex !== undefined) {
			styles.flex = flex;
		}

		if (center) {
			styles.justifyContent = "center";
			styles.alignItems = "center";
		}

		if (absTopLeft) {
			styles.position = "absolute";
			styles.top = 0;
			styles.left = 0;
		}

		if (absBottomRight) {
			styles.position = "absolute";
			styles.bottom = 0;
			styles.right = 0;
		}

		if (border) {
			styles.border = "1px solid var(--mantine-color-default-border)";
			styles.borderRadius = "var(--mantine-radius-sm)";
		}

		if (top) {
			if (column) {
				styles.justifyContent = "flex-start";
			} else {
				styles.alignItems = "flex-start";
			}
		}

		if (bottom) {
			if (column) {
				styles.justifyContent = "flex-end";
			} else {
				styles.alignItems = "flex-end";
			}
		}

		if (right) {
			if (column) {
				styles.alignItems = "flex-end";
			} else {
				styles.justifyContent = "flex-end";
			}
		}

		if (left) {
			if (column) {
				styles.alignItems = "flex-start";
			} else {
				styles.justifyContent = "flex-start";
			}
		}

		if (centerX) {
			if (column) {
				styles.alignItems = "center";
			} else {
				styles.justifyContent = "center";
			}
		}

		if (centerY) {
			if (column) {
				styles.justifyContent = "center";
			} else {
				styles.alignItems = "center";
			}
		}

		if (nowrap) {
			styles.flexWrap = "nowrap";
		}

		if (wrap) {
			styles.flexWrap = "wrap";
		}

		if (shrink) {
			styles.flexShrink = 1;
		}

		if (noshrink) {
			styles.flexShrink = 0;
		}

		if (grow) {
			styles.flexGrow = 1;
		}

		if (nogrow) {
			styles.flexGrow = 0;
		}

		if (spaceBetween) {
			styles.justifyContent = "space-between";
		}

		if (spaceEvenly) {
			styles.justifyContent = "space-evenly";
		}

		if (overflowHidden) {
			styles.overflow = "hidden";
		}

		if (relative) {
			styles.position = "relative";
		}

		if (z !== undefined) {
			styles.zIndex = z;
		}

		if (className) {
			styles.className = className;
		}
		const Component = styles.display === "flex" ? Flex : Box;
		return (
			<Component
				ref={ref}
				style={{
					...styles,
					...(style ?? {}),
				}}
				className={className}
				gap={gap}
				p={p}
				py={py}
				pt={pt}
				mah={mah}
				bg={bg}
				mt={mt}
				mb={mb}
				maw={maw}
				w={w}
				h={h}
				data-testid={dataTestId}
			>
				{children}
			</Component>
		);
	},
);
